import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Route } from "react-router-dom";
import Papa from "papaparse";

export function BulkEmailSender() {
    const [serviceUrl] = useState(
        "https://bulk-email-sender-972105211737.asia-south1.run.app",
    );
    const [csvFile, setCsvFile] = useState(null);
    const [recipients, setRecipients] = useState(""); // public GCS URL after upload
    const [emailTemplate, setEmailTemplate] = useState(
        `<h1>Hello from MathAI!</h1><p>This is a test email from the bulk sender system.</p><p>If you are reading this, everything is working correctly!</p>`,
    );
    const [subject, setSubject] = useState(
        "Test Email - MathAI Bulk Email System",
    );
    const [preheader, setPreheader] = useState("");
    const [categories, setCategories] = useState("test");
    const [unsubscribe, setUnsubscribe] = useState("203139");
    const [secret, setSecret] = useState("");
    const [dryRun, setDryRun] = useState(false);
    const [sendAt, setSendAt] = useState("");
    const [response, setResponse] = useState("Waiting for response...");
    const [responseColor, setResponseColor] = useState("black");
    const [showConfirm, setShowConfirm] = useState(false);
    const [contactsCount, setContactsCount] = useState(null);
    const [contactsCountError, setContactsCountError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [emailTemplateBlobUrl, setEmailTemplateBlobUrl] = useState("");

    const fileInputRef = useRef(null); // ref for file input

    // Utility function for datetime-local minimum
    function getLocalMinDateTimeLocal() {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const date = String(now.getDate()).padStart(2, "0");
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${date}T${hours}:${minutes}`;
    }

    // Generate a blob URL for HTML preview
    useEffect(() => {
        if (!emailTemplate) {
            setEmailTemplateBlobUrl("");
            return;
        }
        const blob = new Blob([emailTemplate], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        setEmailTemplateBlobUrl(url);
        return () => {
            URL.revokeObjectURL(url);
            setEmailTemplateBlobUrl("");
        };
    }, [emailTemplate]);

    // Get signed URL and upload
    async function uploadToGCS({ fileName, bucketName, data, contentType }) {
        try {
            const signedResp = await axios.post(
                `${process.env.REACT_APP_HOMEWORK_SERVER_API_ENDPOINT}/prod/hw/getSignedUrlForUpload`,
                { bucket: bucketName, key: fileName },
            );

            const uploadUrl = signedResp.data?.url;
            const publicUrl = signedResp.data?.bucket_url;
            if (!uploadUrl || !publicUrl) {
                throw new Error("Could not get upload URL");
            }

            const uploadResponse = await fetch(uploadUrl, {
                method: "PUT",
                body: data,
                headers: { "Content-Type": contentType },
            });
            if (!uploadResponse.ok) {
                const errText = await uploadResponse.text();
                throw new Error("Upload failed: " + errText);
            }
            return publicUrl;
        } catch (error) {
            console.error("Error uploading to GCS:", error);
            return null;
        }
    }

    // File input handler with full logging
    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        setCsvFile(file);
        if (!file) return;
        setUploading(true);
        setResponse("Uploading file...");

        const fileName = `uploads/${Date.now()}_${file.name}`;
        const bucketName = "test-hw-pdf";
        const contentType = file.type || "text/csv";

        const publicUrl = await uploadToGCS({
            fileName,
            bucketName,
            data: file,
            contentType,
        });

        setUploading(false);

        if (publicUrl) {
            setRecipients(publicUrl);
            setResponse("Upload successful!");
            setResponseColor("green");
            setContactsCount(null);
            setContactsCountError(null);
        } else {
            setRecipients("");
            setResponse("Upload failed");
            setResponseColor("red");
        }
    };

    // Utility for cache busting
    function addCacheBustParam(url) {
        return url.includes("?")
            ? `${url}&_=${Date.now()}`
            : `${url}?_=${Date.now()}`;
    }

    // Contact count from CSV
    async function fetchContactsCount() {
        if (!recipients) {
            setContactsCount(null);
            setContactsCountError("Recipients CSV URL missing");
            console.log("Recipients CSV URL missing");
            return;
        }
        try {
            const urlWithCacheBust = addCacheBustParam(recipients);
            const res = await fetch(urlWithCacheBust);
            if (!res.ok) {
                setContactsCount(null);
                setContactsCountError(`Failed to fetch CSV: ${res.status}`);
                return;
            }
            const text = await res.text();

            const parsed = Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
            });

            const filteredData = parsed.data.filter((row) =>
                Object.values(row).some((val) => val && val.trim() !== ""),
            );

            setContactsCount(filteredData.length);
            setContactsCountError(null);
        } catch (error) {
            setContactsCount(null);
            setContactsCountError(`Error fetching CSV: ${error.message}`);
            console.error("Error fetching CSV:", error);
        }
    }

    // Human date for scheduled send
    function formatSendAt(dateTimeStr) {
        if (!dateTimeStr) return "Not specified (Immediately)";
        const date = new Date(dateTimeStr);
        if (isNaN(date.getTime())) return "Invalid date";
        const options = {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
            day: "numeric",
            month: "long",
        };
        return date.toLocaleString(undefined, options);
    }

    // Modal
    const openConfirmation = async () => {
        if (
            !serviceUrl ||
            !recipients ||
            !emailTemplate ||
            !subject ||
            !secret
        ) {
            setResponse(
                "Please fill all required fields and upload a CSV file.",
            );
            setResponseColor("red");
            return;
        }
        await fetchContactsCount();
        setShowConfirm(true);
    };

    // Send request
    async function sendRequest() {
        setResponse("Sending...");
        setResponseColor("black");
        if (
            !serviceUrl ||
            !recipients ||
            !emailTemplate ||
            !subject ||
            !secret
        ) {
            setResponse(
                "Please fill all required fields and upload a CSV file.",
            );
            setResponseColor("red");
            return;
        }
        const categoriesArray = categories
            ? categories.split(",").map((c) => c.trim())
            : [];
        const payload = {
            recipients,
            email_template: emailTemplate,
            subject,
            secret,
            dry_run: dryRun,
            contacts_count: contactsCount,
        };
        if (preheader) payload.preheader = preheader;
        if (categoriesArray.length) payload.categories = categoriesArray;
        if (unsubscribe && !isNaN(unsubscribe))
            payload.unsubscribe = Number(unsubscribe);
        if (sendAt) {
            const timestamp = Math.floor(new Date(sendAt).getTime() / 1000);
            if (!isNaN(timestamp)) payload.send_at = timestamp;
        }
        try {
            const res = await fetch(serviceUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const text = await res.text();
            if (!res.ok) {
                setResponse(`Error ${res.status}:\n${text}`);
                setResponseColor("red");
            } else {
                try {
                    const json = JSON.parse(text);
                    setResponse(JSON.stringify(json, null, 2));
                    setResponseColor("green");
                } catch {
                    setResponse(text);
                    setResponseColor("black");
                }
            }
        } catch (err) {
            setResponse("Network error: " + err.message);
            setResponseColor("red");
        }
    }

    // Confirmation modal
    const ConfirmationModal = () => (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.6)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
            }}
        >
            <div
                style={{
                    background: "#fff",
                    borderRadius: 8,
                    maxWidth: 480,
                    width: "90%",
                    padding: "1.5rem",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                    fontFamily:
                        "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                }}
            >
                <h2 style={{ marginTop: 0, marginBottom: "1rem" }}>
                    Confirm Bulk Email Sending
                </h2>
                <p>You are about to send bulk email with these details:</p>
                <ul style={{ lineHeight: 1.5 }}>
                    <li>
                        <strong>Contacts:</strong>{" "}
                        {contactsCount !== null
                            ? contactsCount
                            : contactsCountError
                            ? contactsCountError
                            : "Loading..."}
                    </li>
                    <li>
                        <strong>Sending at:</strong> {formatSendAt(sendAt)}
                    </li>
                    <li>
                        <strong>Subject:</strong>{" "}
                        <span
                            dangerouslySetInnerHTML={{
                                __html: subject || "<i>(empty)</i>",
                            }}
                        />
                    </li>
                    <li>
                        <strong>Preheader:</strong>{" "}
                        {preheader ? (
                            <span
                                dangerouslySetInnerHTML={{ __html: preheader }}
                            />
                        ) : (
                            <i>(none)</i>
                        )}
                    </li>
                    <li>
                        <strong>Categories:</strong>{" "}
                        {categories ? categories : <i>(none)</i>}
                    </li>
                    <li>
                        <strong>Body:</strong>{" "}
                        <a
                            href={emailTemplateBlobUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: "#007bff" }}
                        >
                            View HTML
                        </a>
                    </li>
                    <li>
                        <strong>Sending Mode:</strong>{" "}
                        {dryRun ? "Dry Run (simulate)" : "Real Emails WILL GO"}
                    </li>
                </ul>
                <div
                    style={{
                        marginTop: "1.5rem",
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "1rem",
                    }}
                >
                    <button
                        onClick={() => setShowConfirm(false)}
                        style={{
                            padding: "0.5rem 1rem",
                            fontSize: "1rem",
                            borderRadius: 6,
                            border: "1px solid #999",
                            background: "#eee",
                            cursor: "pointer",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            setShowConfirm(false);
                            sendRequest();
                        }}
                        style={{
                            padding: "0.5rem 1.5rem",
                            fontSize: "1rem",
                            borderRadius: 6,
                            border: "none",
                            backgroundColor: "#007bff",
                            color: "#fff",
                            cursor: "pointer",
                        }}
                    >
                        Send
                    </button>
                </div>
            </div>
        </div>
    );

    // Final render
    return (
        <div
            style={{
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                maxWidth: 700,
                margin: "2rem auto",
                padding: "2rem",
                background: "#f0f4f8",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: 10,
            }}
        >
            <h1 style={{ textAlign: "center", marginBottom: "2rem" }}>
                Bulk Email Sender
            </h1>
            <div style={{ marginBottom: "1rem" }}>
                <label style={{ fontWeight: 600 }}>
                    Upload Recipients CSV{" "}
                    <span style={{ color: "red" }}>*</span>
                </label>
                <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    disabled={uploading}
                    ref={fileInputRef}
                    style={{
                        display: "block",
                        width: "100%",
                        marginTop: 8,
                        marginBottom: 12,
                    }}
                />
                {uploading && (
                    <p style={{ color: "#007bff" }}>Uploading CSV file...</p>
                )}
                {recipients && (
                    <p>
                        CSV uploaded:{" "}
                        <a href={recipients} target="_blank" rel="noreferrer">
                            {recipients}
                        </a>
                        <button
                            onClick={() => {
                                setRecipients("");
                                setCsvFile(null);
                                setContactsCount(null);
                                setContactsCountError(null);
                                setResponse("CSV file removed.");
                                setResponseColor("black");
                                if (fileInputRef.current) {
                                    fileInputRef.current.value = null;
                                }
                            }}
                            style={{
                                marginLeft: 10,
                                padding: "0.2rem 0.5rem",
                                fontSize: "0.85rem",
                                cursor: "pointer",
                            }}
                            title="Remove uploaded CSV"
                        >
                            Remove
                        </button>
                    </p>
                )}
            </div>
            <div style={{ marginBottom: "1rem" }}>
                <label style={{ fontWeight: 600 }}>
                    Email Template (HTML){" "}
                    <span style={{ color: "red" }}>*</span>
                </label>
                <textarea
                    value={emailTemplate}
                    onChange={(e) => setEmailTemplate(e.target.value)}
                    rows={6}
                    required
                    style={{
                        width: "100%",
                        padding: "0.5rem 0.75rem",
                        border: "1px solid #ccc",
                        borderRadius: 6,
                        fontSize: "1rem",
                        marginTop: 8,
                        resize: "vertical",
                    }}
                />
            </div>
            <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                placeholder="Subject *"
                style={inputStyle}
            />
            <input
                type="text"
                value={preheader}
                onChange={(e) => setPreheader(e.target.value)}
                placeholder="Preheader (Optional)"
                style={inputStyle}
            />
            <input
                type="text"
                value={categories}
                onChange={(e) => setCategories(e.target.value)}
                placeholder="Categories (Comma separated)"
                style={inputStyle}
            />
            <input
                type="number"
                value={unsubscribe}
                onChange={(e) => setUnsubscribe(e.target.value)}
                placeholder="Unsubscribe ID (Optional)"
                style={inputStyle}
            />
            <input
                type="datetime-local"
                value={sendAt}
                min={getLocalMinDateTimeLocal()}
                onChange={(e) => setSendAt(e.target.value)}
                placeholder="Send At (Specific Date & Time, optional)"
                style={inputStyle}
            />
            <input
                type="text"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                required
                placeholder="Secret Key *"
                style={inputStyle}
            />
            <label
                style={{ display: "flex", alignItems: "center", marginTop: 12 }}
            >
                <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    style={{ marginRight: 8, transform: "scale(1.2)" }}
                />
                Dry Run (simulate)
            </label>
            <button
                onClick={openConfirmation}
                style={{
                    marginTop: "1.5rem",
                    display: "block",
                    width: "100%",
                    padding: "1rem",
                    backgroundColor: "#007bff",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    fontSize: "1.2rem",
                    cursor: "pointer",
                }}
                disabled={uploading}
            >
                Send Bulk Email
            </button>
            <h2 style={{ marginTop: "2rem" }}>Response</h2>
            <pre
                style={{
                    background: "#fff",
                    color: responseColor,
                    padding: "1rem",
                    borderRadius: 6,
                    maxHeight: 300,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                }}
            >
                {response}
            </pre>
            {showConfirm && <ConfirmationModal />}
        </div>
    );
}

const inputStyle = {
    width: "100%",
    padding: "0.5rem 0.75rem",
    marginTop: 8,
    marginBottom: 12,
    border: "1px solid #ccc",
    borderRadius: 6,
    fontSize: "1rem",
    boxSizing: "border-box",
};

export const SEND_EMAIL_ROUTE = [
    <Route path="/bulkEmailSender" element={<BulkEmailSender />} />,
];
